import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from '../context/ProductStore';

import ProductCard from '../components/ProductCard';
import CartSidebar from '../components/CartSidebar';
import CheckoutModal from '../components/CheckoutModal';
import AdminPanel from '../components/AdminPanel';
import QuoteRequestModal from '../components/QuoteRequestModal';

const BRANDS = {
  Laptops:      ['All Brands', 'HP', 'Dell', 'Lenovo', 'Asus', 'Acer', 'Samsung', 'Apple'],
  Phones:       ['All Brands', 'Samsung', 'iPhone', 'Huawei', 'Tecno', 'Infinix', 'Nokia'],
  Desktops:     ['All Brands', 'HP', 'Dell', 'Lenovo', 'Asus', 'Acer'],
  Printers:     ['All Brands', 'HP', 'Epson', 'Canon', 'Brother', 'Samsung'],
  Networking:   ['All Brands', 'Starlink', 'TP-Link', 'Huawei', 'Mikrotik', 'Cisco', 'Ubiquiti'],
  'PA Systems': ['All Brands', 'JBL', 'Yamaha', 'Sony', 'Bose', 'Behringer', 'Mackie'],
  Accessories:  ['All Brands', 'Logitech', 'HP', 'Dell', 'Samsung', 'Generic'],
  'Laptop Bags':['All Brands', 'Targus', 'HP', 'Dell', 'Samsonite', 'Generic'],
};

const FOR_TAGS = ['Business', 'Executive', 'Students', 'Gaming'];

// Subcategories shown in flyout panel on hover
const SUBCATEGORIES = {
  Laptops:      ['All Laptops', 'Business Laptops', 'Gaming Laptops', 'Student Laptops', '2-in-1 Laptops', 'Ultrabooks'],
  Desktops:     ['All in One Desktops', 'Desktops', 'Monitors', 'Power Supply & Graphic Cards'],
  Printers:     ['All Printers', 'Inkjet Printers', 'Laser Printers', 'Multifunction Printers', 'Ink & Toners'],
  Phones:       ['All Phones', 'Smartphones', 'Tablets', 'Feature Phones', 'Phone Accessories'],
  Networking:   ['All Networking', 'Routers & Switches', 'Starlink', 'WiFi Extenders', 'Cables & Adapters', 'Mikrotik'],
  Accessories:  ['All Accessories', 'Keyboards & Mice', 'Headsets & Audio', 'Webcams', 'USB Hubs', 'Cables'],
  'PA Systems': ['All PA Systems', 'Speakers', 'Microphones', 'Mixers', 'Amplifiers'],
  'Laptop Bags':['All Bags', 'Backpacks', 'Sleeve Cases', 'Trolley Bags', 'Messenger Bags'],
};

// Category item — click to expand subcategories + brands inline below
const CategoryItem = ({ cat, isActive, onSelect, isLast, activeBrand, activeSubType, onBrandSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const label = cat === 'All' ? 'All categories' : cat.toUpperCase();
  const subs = SUBCATEGORIES[cat] || [];
  const brands = (BRANDS[cat] || []).filter(b => b !== 'All Brands');

  const handleClick = () => {
    onSelect(cat);
    if (cat !== 'All') setExpanded(e => !e);
  };

  useEffect(() => {
    if (isActive && cat !== 'All') setExpanded(true);
    if (!isActive) setExpanded(false);
  }, [isActive, cat]);

  return (
    <div>
      {/* Main category row */}
      <button
        onClick={handleClick}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', textAlign: 'left',
          padding: '11px 16px',
          border: 'none',
          background: isActive ? '#eef0fb' : 'transparent',
          color: isActive ? '#CC0000' : '#5a6070',
          fontWeight: isActive ? 700 : 500,
          fontSize: 13,
          cursor: 'pointer',
          fontFamily: "'Inter', sans-serif",
          letterSpacing: '0.04em',
          transition: 'all 0.12s',
          borderLeft: isActive ? '3px solid #CC0000' : '3px solid transparent',
        }}
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = '#f5f6ff'; e.currentTarget.style.color = '#CC0000'; }}}
        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#5a6070'; }}}
      >
        <span>{label}</span>
        {cat !== 'All' && (
          <span style={{ fontSize: 11, color: '#aaa', transform: expanded ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▾</span>
        )}
      </button>

      {/* Expanded panel — subcategories + brands */}
      {expanded && cat !== 'All' && (
        <div style={{ background: '#f8f9ff', borderTop: '1px solid #e8eaf0', borderBottom: '1px solid #e8eaf0', padding: '8px 0' }}>

          {/* Subcategory types */}
          {subs.length > 0 && (
            <div style={{ padding: '4px 16px 6px' }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#CC0000', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>TYPES</div>
              {/* "All types" clear option */}
              <button
                onClick={() => onSelect(cat, '')}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px',
                  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                  fontFamily: "'Inter', sans-serif",
                  background: !activeSubType ? '#fce8e8' : 'transparent',
                  color: !activeSubType ? '#CC0000' : '#9ca3af',
                  fontWeight: !activeSubType ? 700 : 400,
                }}
              >
                All {cat}
              </button>
              {subs.map(sub => {
                const isSubActive = activeSubType === sub;
                return (
                  <button key={sub} onClick={() => onSelect(cat, sub)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px',
                      border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                      fontFamily: "'Inter', sans-serif",
                      background: isSubActive ? '#fce8e8' : 'transparent',
                      color: isSubActive ? '#CC0000' : '#374151',
                      fontWeight: isSubActive ? 700 : 400,
                    }}
                    onMouseEnter={e => { if (!isSubActive) { e.currentTarget.style.background = '#e8ecff'; e.currentTarget.style.color = '#CC0000'; }}}
                    onMouseLeave={e => { if (!isSubActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#374151'; }}}
                  >
                    › {sub}
                  </button>
                );
              })}
            </div>
          )}

          {/* Brand pills */}
          {brands.length > 0 && (
            <div style={{ padding: '6px 16px 4px', borderTop: subs.length > 0 ? `1px solid ${'#e8eaf0'}` : 'none', marginTop: subs.length > 0 ? 4 : 0 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#CC0000', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>BRANDS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {brands.map(brand => {
                  const isBrandActive = activeBrand === brand;
                  return (
                    <button key={brand} onClick={() => onBrandSelect(brand)}
                      style={{
                        padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                        cursor: 'pointer', fontFamily: "'Inter', sans-serif", transition: 'all 0.12s',
                        border: isBrandActive ? 'none' : '1px solid #d1d5db',
                        background: isBrandActive ? '#CC0000' : '#fff',
                        color: isBrandActive ? '#fff' : '#374151',
                        boxShadow: isBrandActive ? '0 2px 6px rgba(10,17,114,0.3)' : 'none',
                      }}
                    >
                      {brand}
                    </button>
                  );
                })}
              </div>
              {/* Active filter summary */}
              {(activeBrand !== 'All Brands' || activeSubType) && (
                <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>Filtering:</span>
                  {activeSubType && <span style={{ background: '#fce8e8', color: '#CC0000', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{activeSubType}</span>}
                  {activeBrand !== 'All Brands' && <span style={{ background: '#fce8e8', color: '#CC0000', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{activeBrand}</span>}
                  <button onClick={() => { onSelect(cat, ''); onBrandSelect('__clear__'); }}
                    style={{ background: 'none', border: 'none', color: '#CC0000', fontSize: 11, cursor: 'pointer', fontWeight: 700, padding: 0 }}>
                    ✕ Clear
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!isLast && !expanded && (
        <div style={{ height: 1, background: '#f0f0f0', margin: '0 12px' }} />
      )}
    </div>
  );
};


const CUSTOMERS_KEY = 'techdave_customers';
const SESSION_KEY   = 'techdave_session';
const getCustomers  = () => JSON.parse(localStorage.getItem(CUSTOMERS_KEY) || '[]');
const saveCustomers = (list) => localStorage.setItem(CUSTOMERS_KEY, JSON.stringify(list));
const getSession    = () => JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
const saveSession   = (u) => localStorage.setItem(SESSION_KEY, JSON.stringify(u));
const clearSession  = () => localStorage.removeItem(SESSION_KEY);

const Shop = () => {
  const {
    products, inStockProducts, outOfStockProducts, promoProducts,
    cartCount, adminMode, loginAdmin, logoutAdmin,
  } = useStore();
  
  const location = useLocation();
  

  const [activeCategory, setActiveCategory] = useState('All');
  const [activeBrand,    setActiveBrand]    = useState('All Brands');
  const [activeSubType,  setActiveSubType]  = useState('');
  const [activeForTag,   setActiveForTag]   = useState('All');
  const [showCart,       setShowCart]       = useState(false);
  const [showCheckout,   setShowCheckout]   = useState(false);
  const [showAdmin,      setShowAdmin]      = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showQuote,      setShowQuote]      = useState(false);
  const [adminPin,       setAdminPin]       = useState('');
  const [pinError,       setPinError]       = useState('');
  const [pinLoading,     setPinLoading]     = useState(false);
  const [minPrice,       setMinPrice]       = useState('');
  const [maxPrice,       setMaxPrice]       = useState('');
  const [inStockOnly,    setInStockOnly]    = useState(false);
  const [showFilters,    setShowFilters]    = useState(false);
  const [customer,       setCustomer]       = useState(getSession());

  const handleCategorySelect = (cat, sub) => {
    setActiveCategory(cat);
    setActiveBrand('All Brands');
    setActiveSubType('');
    if (sub) setActiveSubType(sub);
  };

  const cycleForTag = (dir) => {
    setActiveForTag(prev => {
      const idx = FOR_TAGS.indexOf(prev);
      if (idx === -1) return dir === 1 ? FOR_TAGS[0] : FOR_TAGS[FOR_TAGS.length - 1];
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= FOR_TAGS.length) return 'All';
      return FOR_TAGS[nextIdx];
    });
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('category');
    if (cat) { setActiveCategory(cat); setActiveBrand('All Brands'); }
    const forTag = params.get('for');
    if (forTag) { setActiveForTag(forTag); }
  }, [location.search]);

  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setAdminPin('');
        setPinError('');
        setShowAdminLogin(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleAdminLogin = async () => {
    if (!adminPin) return;
    setPinError('');
    setPinLoading(true);
    const result = await loginAdmin(adminPin);
    setPinLoading(false);
    if (result.success) {
      setShowAdminLogin(false);
      setShowAdmin(true);
      setAdminPin('');
    } else {
      setPinError(result.error || 'Incorrect PIN. Try again.');
      setAdminPin('');
      setTimeout(() => setPinError(''), 3000);
    }
  };

  const filteredProducts = products.filter(p => {
    const matchCat     = activeCategory === 'All' || p.category === activeCategory;
    const matchBrand   = activeBrand === 'All Brands' ||
      (p.brand || '').toLowerCase() === activeBrand.toLowerCase();
    const matchSubType = !activeSubType || (
      (p.name || '').toLowerCase().includes(activeSubType.toLowerCase()) ||
      (p.subCategory || '').toLowerCase().includes(activeSubType.toLowerCase()) ||
      (p.tags || []).some(t => t.toLowerCase().includes(activeSubType.toLowerCase()))
    );
    const matchFor     = activeForTag === 'All' || (p.forTags || []).includes(activeForTag);
    const price        = parseFloat(p.isPromo ? p.promoPrice : p.price) || 0;
    const matchMin     = !minPrice || price >= parseFloat(minPrice);
    const matchMax     = !maxPrice || price <= parseFloat(maxPrice);
    const matchStock   = !inStockOnly || !p.outOfStock;
    return matchCat && matchBrand && matchSubType && matchFor && matchMin && matchMax && matchStock;
  });

  const activeFilterCount = [activeBrand !== 'All Brands'].filter(Boolean).length;

  // All categories for sidebar
  const ALL_SIDEBAR_CATS = [
    'All',
    'Laptops',
    'Desktops',
    'Printers',
    'Phones',
    'Networking',
    'Accessories',
    'PA Systems',
    'Laptop Bags',
  ];

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        .shop-root * { font-family: 'League Spartan', sans-serif; }
        .prod-grid {
          display: grid; gap: 1px; background: #e5e7eb;
          grid-template-columns: repeat(3,1fr);
        }
        @media(max-width:900px){ .prod-grid{ grid-template-columns: repeat(2,1fr); } }
        @media(max-width:400px){ .prod-grid{ grid-template-columns: 1fr; } }
        .filter-drawer{ position:fixed;inset:0;z-index:900;display:flex;align-items:flex-end; }
        .filter-backdrop{ position:absolute;inset:0;background:rgba(0,0,0,0.6);backdrop-filter:blur(4px); }
        .filter-sheet{
          position:relative;z-index:1;width:100%;max-height:85vh;
          border-radius:20px 20px 0 0;padding:20px 20px 40px;
          overflow-y:auto;display:flex;flex-direction:column;gap:16px;
        }
        .sheet-handle{ width:40px;height:4px;border-radius:2px;background:rgba(128,128,128,0.3);margin:0 auto 4px; }
        .cat-item-btn:hover { background: #f0f4ff !important; color: #CC0000 !important; }
        .pin-field { caret-color: #CC0000; }
        .pin-field:focus { border-color: rgba(204,0,0,0.6) !important; }
        .for-tabs-scroll::-webkit-scrollbar { display: none; }
      `}</style>

      <main className="shop-root" style={S.main()}>

        {/* Admin stats bar */}
        {adminMode && (
          <div style={S.adminBar}>
            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
              <div style={S.statPill('green')}>✅ {inStockProducts.length}</div>
              <div style={S.statPill('red')}>❌ {outOfStockProducts.length}</div>
              <div style={S.statPill('orange')}>🔥 {promoProducts.length}</div>
              <div style={S.statPill('gray')}>📦 {products.length}</div>
            </div>
            <div style={{ display:'flex', gap:'6px' }}>
              <button onClick={() => setShowAdmin(true)} style={S.adminBtn}>🛠️ Admin</button>
              <button onClick={logoutAdmin} style={S.logoutBtn}>Logout</button>
            </div>
          </div>
        )}

        <div style={S.container}>

          {/* Welcome banner */}
          {customer && (
            <div style={S.welcomeBanner}>
              <span style={{ fontSize:'22px' }}>👋</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={S.welcomeTitle}>Welcome back, {customer.name.split(' ')[0]}!</div>
                <div style={S.welcomeSub}>{customer.isRegular ? '⭐ Regular Customer — enjoy exclusive discounts!' : 'Keep shopping to unlock Regular Customer discounts 🎁'}</div>
              </div>
            </div>
          )}

          <div style={S.pageHeader}>
            <h1 style={S.title()}>{activeCategory !== 'All' ? activeCategory : 'Browse Products'}</h1>
            <p style={S.sub}>Quality technology at competitive prices · Harare</p>
            <button
              onClick={() => setShowQuote(true)}
              style={{ marginTop:12, padding:'10px 24px', background:'#CC0000', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6, fontFamily:"'Inter', sans-serif", boxShadow:'0 2px 8px rgba(204,0,0,0.25)' }}>
              📄 Request a Quotation
            </button>
          </div>


          <div style={S.layout}>

            {/* ── Desktop sidebar: Categories only ── */}
            {!isMobile && (
              <div style={S.sidebar()}>
                {/* Navy header */}
                <div style={S.catHeader}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="white" style={{ flexShrink: 0 }}>
                    <circle cx="3" cy="3" r="2"/><circle cx="9" cy="3" r="2"/><circle cx="15" cy="3" r="2"/>
                    <circle cx="3" cy="9" r="2"/><circle cx="9" cy="9" r="2"/><circle cx="15" cy="9" r="2"/>
                    <circle cx="3" cy="15" r="2"/><circle cx="9" cy="15" r="2"/><circle cx="15" cy="15" r="2"/>
                  </svg>
                  <span style={S.catHeaderText}>All Categories</span>
                </div>

                {/* Category list */}
                <div style={S.catList()}>
                  {ALL_SIDEBAR_CATS.map((cat, i) => (
                    <CategoryItem
                      key={cat}
                      cat={cat}
                      isActive={activeCategory === cat}
                      
                      onSelect={handleCategorySelect}
                      isLast={i === ALL_SIDEBAR_CATS.length - 1}
                      activeBrand={activeBrand}
                      activeSubType={activeSubType}
                      onBrandSelect={(brand) => {
                        if (brand === '__clear__') { setActiveBrand('All Brands'); setActiveSubType(''); }
                        else setActiveBrand(prev => prev === brand ? 'All Brands' : brand);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div style={{ flex:1, minWidth:0 }}>

              {/* Mobile filter toggle */}
              {isMobile && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px' }}>
                  <button onClick={() => setShowFilters(true)} style={S.filterToggleBtn()}>
                    ⚙️ Filters {activeFilterCount > 0 && <span style={S.filterBadge}>{activeFilterCount}</span>}
                  </button>
                  <span style={{ fontSize:'13px', color:'rgba(128,128,128,0.8)' }}>
                    {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}

              {filteredProducts.length > 0 ? (
                <div className="prod-grid">
                  {filteredProducts.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div style={S.empty()}>
                  <span style={{ fontSize:'48px' }}>📦</span>
                  <p style={S.emptyTitle}>
                    {activeForTag !== 'All'
                      ? `No products tagged "${activeForTag}" yet`
                      : activeBrand !== 'All Brands'
                        ? `No ${activeBrand} products in ${activeCategory}`
                        : 'No products yet'}
                  </p>
                  <p style={S.emptySub}>{adminMode ? 'Open Admin Panel to add products' : 'Check back soon for new arrivals'}</p>
                  {adminMode && <button onClick={() => setShowAdmin(true)} style={S.addFirstBtn}>➕ Add Your First Product</button>}
                </div>
              )}
            </div>
          </div>
        </div>

        {isMobile && showFilters && (
          <div className="filter-drawer">
            <div className="filter-backdrop" onClick={() => setShowFilters(false)} />
            <div className="filter-sheet" style={{ backgroundColor: '#fff' }}>
              <div className="sheet-handle" />
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:'17px', fontWeight:'800', color: '#111' }}>Categories</span>
                <button onClick={() => setShowFilters(false)}
                  style={{ background:'rgba(204,0,0,0.1)', border:'none', borderRadius:'50%', width:'32px', height:'32px', fontSize:'16px', cursor:'pointer', color:'#CC0000' }}>✕</button>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {ALL_SIDEBAR_CATS.map(cat => (
                  <button key={cat}
                    onClick={() => { setActiveCategory(cat); setActiveBrand('All Brands'); setShowFilters(false); }}
                    style={{ padding:'7px 16px', borderRadius:20, border:'none', fontWeight:600, fontSize:13, cursor:'pointer',
                      background: activeCategory === cat ? '#CC0000' : 'rgba(0,0,0,0.06)',
                      color: activeCategory === cat ? '#fff' : '#333' }}>
                    {cat === 'All' ? 'All categories' : cat}
                  </button>
                ))}
              </div>
              <button onClick={() => setShowFilters(false)}
                style={{ padding:'14px', borderRadius:'10px', border:'none', background:'linear-gradient(135deg,#CC0000,#990000)', color:'#fff', fontWeight:'700', fontSize:'15px', cursor:'pointer' }}>
                Show {filteredProducts.length} Result{filteredProducts.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}

        {/* Admin login modal */}
        {showAdminLogin && (
          <div style={S.overlay}>
            <div style={S.pinModal}>
              <h3 style={S.pinTitle}>🔐 Admin Access</h3>
              <p style={S.pinSub}>Enter your admin PIN to manage products</p>
              <input
                className="pin-field"
                type="password"
                placeholder="Enter PIN"
                value={adminPin}
                onChange={e => setAdminPin(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdminLogin()}
                style={S.pinInput}
                autoFocus
                autoComplete="off"
                spellCheck={false}
              />
              {pinError && <p style={S.pinError}>{pinError}</p>}
              <div style={S.pinBtns}>
                <button onClick={() => { setShowAdminLogin(false); setAdminPin(''); setPinError(''); }} style={S.pinCancel}>Cancel</button>
                <button onClick={handleAdminLogin} disabled={pinLoading} style={{ ...S.pinSubmit, opacity: pinLoading ? 0.6 : 1 }}>
                  {pinLoading ? 'Checking...' : 'Login →'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showAdmin    && <AdminPanel onClose={() => setShowAdmin(false)} />}
        {showCart     && <CartSidebar onClose={() => setShowCart(false)} onCheckout={() => { setShowCart(false); setShowCheckout(true); }} />}
        {showCheckout && <CheckoutModal onClose={() => setShowCheckout(false)} />}
        {showQuote    && <QuoteRequestModal onClose={() => setShowQuote(false)} />}

        {cartCount > 0 && !showCart && (
          <button onClick={() => setShowCart(true)} style={S.floatingCart}>
            🛒 <span style={S.floatingCartCount}>{cartCount}</span> View Cart
          </button>
        )}
      </main>
    </>
  );
};

/* ─── Styles ─────────────────────────────────────────────── */
const S = {
  main: () => ({ minHeight:'100vh', paddingTop:'120px', backgroundColor: '#ffffff', color: '#1a1a1a' }),
  adminBar: { backgroundColor:'#fff', borderBottom:'1px solid #e5e7eb', padding:'6px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 },
  statPill: (c) => ({ fontSize:'11px', fontWeight:'700', padding:'3px 8px', borderRadius:'100px', backgroundColor: c==='green'?'rgba(22,163,74,0.15)':c==='red'?'rgba(220,38,38,0.15)':c==='orange'?'rgba(217,119,6,0.15)':'rgba(0,0,0,0.05)', color: c==='green'?'#16a34a':c==='red'?'#dc2626':c==='orange'?'#d97706':'#374151', border:`1px solid ${c==='green'?'rgba(22,163,74,0.3)':c==='red'?'rgba(220,38,38,0.3)':c==='orange'?'rgba(217,119,6,0.3)':'rgba(0,0,0,0.12)'}` }),
  adminBtn: { padding:'6px 12px', borderRadius:'8px', border:'none', background:'linear-gradient(135deg,#CC0000,#990000)', color:'#fff', fontWeight:'700', fontSize:'12px', cursor:'pointer' },
  logoutBtn:{ padding:'6px 12px', borderRadius:'8px', border:'1px solid rgba(0,0,0,0.15)', background:'transparent', color:'#374151', fontWeight:'600', fontSize:'12px', cursor:'pointer' },

  container: { maxWidth:'1280px', margin:'0 auto', padding:'16px 12px 60px' },

  welcomeBanner: { background:'linear-gradient(135deg,rgba(204,0,0,0.12),rgba(153,0,0,0.06))', border:'1px solid rgba(204,0,0,0.25)', borderRadius:'12px', padding:'12px 14px', marginBottom:'14px', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' },
  welcomeTitle:  { fontSize:'14px', fontWeight:'800', color:'#CC0000' },
  welcomeSub:    { fontSize:'12px', color:'rgba(128,128,128,0.9)', marginTop:'2px' },

  pageHeader: { textAlign:'center', marginBottom:'16px' },
  title: () => ({ fontSize:'clamp(22px,5vw,44px)', fontWeight:'900', color: '#1a1a1a', letterSpacing:'-0.02em', margin:'0 0 4px' }),
  sub:   { fontSize:'13px', color:'rgba(128,128,128,0.9)', margin:0 },

  brandSection: { marginBottom:'16px', textAlign:'center' },
  brandLabel:   { fontSize:'11px', fontWeight:'700', color:'#CC0000', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'8px' },
  brandFilters: { display:'flex', gap:'6px', flexWrap:'wrap', justifyContent:'center', marginBottom:'8px' },
  brandBtn: (active) => ({ padding:'6px 12px', borderRadius:'100px', background: active?'#111111':(dark?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.06)'), color: active?'#fff':(dark?'rgba(255,255,255,0.8)':'#333'), fontWeight:'700', fontSize:'12px', cursor:'pointer', border: active?'none':`1px solid ${dark?'rgba(255,255,255,0.12)':'rgba(0,0,0,0.15)'}` }),

  layout:  { display:'flex', gap:'16px', alignItems:'flex-start' },

  /* Sidebar — overflow visible so flyout panels aren't clipped */
  sidebar: () => ({
    width: '220px', flexShrink: 0,
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    overflow: 'visible',
    display: 'flex', flexDirection: 'column',
    position: 'sticky', top: '130px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  }),

  /* Navy header bar */
  catHeader: {
    background: '#CC0000',
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '14px 16px',
    borderRadius: '8px 8px 0 0',
  },
  catHeaderText: {
    color: '#fff', fontWeight: 800, fontSize: 15,
    fontFamily: "'Inter', sans-serif", letterSpacing: '0.01em',
  },

  /* Category list container */
  catList: () => ({
    display: 'flex', flexDirection: 'column',
    backgroundColor: '#fff',
    overflow: 'visible',
    position: 'relative',
  }),

  /* Individual category button */
  catItem: (active, dark) => ({
    display: 'block', width: '100%', textAlign: 'left',
    padding: '11px 16px',
    border: 'none', background: active ? (dark ? '#1a2080' : '#e8ecff') : 'transparent',
    color: active ? '#CC0000' : (dark ? 'rgba(255,255,255,0.75)' : '#5a6a3a'),
    fontWeight: active ? 700 : 500,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    letterSpacing: '0.04em',
    transition: 'all 0.12s',
  }),

  catDivider: () => ({
    height: 1,
    background: '#f0f0f0',
    margin: '0 12px',
  }),

  filterToggleBtn: () => ({ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'8px', border:'1px solid #e5e7eb', background: '#fff', color: '#111', fontWeight:'700', fontSize:'13px', cursor:'pointer' }),
  filterBadge: { backgroundColor:'#CC0000', color:'#fff', borderRadius:'50%', width:'18px', height:'18px', fontSize:'10px', fontWeight:'900', display:'inline-flex', alignItems:'center', justifyContent:'center', marginLeft:'2px' },

  filterGroup: { display:'flex', flexDirection:'column', gap:'8px', padding: '0 16px' },
  filterTitle: () => ({ fontSize:'11px', fontWeight:'900', letterSpacing:'0.12em', color: '#111', textTransform:'uppercase', paddingBottom:'8px', borderBottom:'2px solid #CC0000', marginBottom:'4px' }),
  checkLabel: () => ({ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', fontWeight:'500', color: dark?'rgba(255,255,255,0.8)':'#374151', cursor:'pointer' }),
  checkbox:    { accentColor:'#CC0000', width:'14px', height:'14px', cursor:'pointer' },
  priceInput: () => ({ flex:1, padding:'6px 8px', borderRadius:'8px', border:'1.5px solid #e5e7eb', fontSize:'12px', fontWeight:'600', outline:'none', backgroundColor: '#f9fafb', color: '#111', width:'100%' }),
  clearBtn:    { margin:'0 16px 12px', padding:'7px 12px', borderRadius:'8px', border:'1.5px solid #CC0000', background:'rgba(204,0,0,0.06)', color:'#CC0000', fontWeight:'700', fontSize:'12px', cursor:'pointer' },

  empty: () => ({ textAlign:'center', padding:'50px 20px', backgroundColor: '#fff', borderRadius:'16px', display:'flex', flexDirection:'column', alignItems:'center', gap:'10px', border:'1px solid rgba(204,0,0,0.1)' }),
  emptyTitle:  { fontSize:'17px', fontWeight:'700', margin:0 },
  emptySub:    { fontSize:'13px', color:'rgba(128,128,128,0.7)', margin:0 },
  addFirstBtn: { marginTop:'6px', padding:'10px 20px', borderRadius:'10px', border:'none', background:'linear-gradient(135deg,#CC0000,#990000)', color:'#fff', fontWeight:'700', fontSize:'13px', cursor:'pointer' },

  overlay:  { position:'fixed', inset:0, zIndex:9995, backgroundColor:'rgba(0,0,0,0.85)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' },
  pinModal: { backgroundColor:'#1a1a1a', borderRadius:'20px', padding:'28px 20px', width:'100%', maxWidth:'360px', textAlign:'center', border:'1px solid rgba(204,0,0,0.3)', display:'flex', flexDirection:'column', gap:'12px' },
  pinTitle: { fontSize:'20px', fontWeight:'800', color:'#fff', margin:0 },
  pinSub:   { fontSize:'13px', color:'rgba(255,255,255,0.5)', margin:0 },
  pinInput: { backgroundColor:'#242424', border:'1.5px solid rgba(204,0,0,0.3)', borderRadius:'12px', padding:'12px 16px', color:'#fff', fontSize:'18px', textAlign:'center', letterSpacing:'0.1em', fontFamily:'inherit', outline:'none', width:'100%', transition:'border-color 0.2s' },
  pinError: { color:'#f87171', fontSize:'13px', fontWeight:'600', margin:0 },
  pinBtns:  { display:'flex', gap:'10px' },
  pinCancel:{ flex:1, padding:'11px', borderRadius:'10px', border:'1.5px solid rgba(255,255,255,0.15)', background:'transparent', color:'#fff', fontWeight:'600', cursor:'pointer' },
  pinSubmit:{ flex:1, padding:'11px', borderRadius:'10px', border:'none', background:'linear-gradient(135deg,#CC0000,#990000)', color:'#fff', fontWeight:'700', cursor:'pointer' },

  floatingCart:      { position:'fixed', bottom:'80px', left:'50%', transform:'translateX(-50%)', zIndex:500, padding:'11px 20px', borderRadius:'100px', border:'none', background:'linear-gradient(135deg,#CC0000,#990000)', color:'#fff', fontWeight:'700', fontSize:'13px', cursor:'pointer', boxShadow:'0 4px 20px rgba(204,0,0,0.5)', display:'flex', alignItems:'center', gap:'6px', whiteSpace:'nowrap' },
  floatingCartCount: { backgroundColor:'#fff', color:'#CC0000', borderRadius:'50%', width:'20px', height:'20px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:'900' },

  forTabsWrap: { display:'flex', alignItems:'center', gap:'14px', marginBottom:'16px', borderBottom:'1px solid rgba(128,128,128,0.15)', paddingBottom:'10px', width:'100%' },
  forLabel: () => ({ fontSize:'18px', fontWeight:'900', letterSpacing:'0.05em', color: '#111', flexShrink:0 }),
  forTabs: { display:'flex', gap:'24px', flexWrap:'nowrap', overflowX:'auto', flex:'1 1 auto', minWidth:0, scrollbarWidth:'none' },
  forTab: (active) => ({ padding:'4px 0', border:'none', background:'none', cursor:'pointer', fontSize:'13px', fontWeight:'800', letterSpacing:'0.06em', whiteSpace:'nowrap', color: active ? '#CC0000' : (dark ? 'rgba(255,255,255,0.6)' : '#666'), borderBottom: active ? '2px solid #CC0000' : '2px solid transparent' }),
  forArrows: { display:'flex', gap:'8px', flexShrink:0, marginLeft:'auto', paddingLeft:'12px' },
  forArrowBtn: () => ({ width:'32px', height:'32px', borderRadius:'50%', border:`1.5px solid ${dark?'rgba(255,255,255,0.25)':'rgba(0,0,0,0.2)'}`, background: dark?'rgba(255,255,255,0.08)':'#f3f4f6', color: dark?'#fff':'#333', fontSize:'18px', fontWeight:'900', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, lineHeight:1 }),
};

export default Shop;
